import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  CircularProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAccessRolesWithPermissionsQuery } from "Api/Queries/AccessRole/useAccessRolesWithPermissionsQuery";
import type {
  AccessRightPermissionDto,
  AccessRoleWithPermissionsDto,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import {
  type MRT_ColumnDef,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";

type FlattenedPermissionRow = {
  roleId: number;
  roleCode: string;
  roleName: string;
  accessRightCode: string;
  canRead: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canExport: boolean;
};

const RolesPermissionsResources =
  Resources.Pages.Content.Users.RolesPermissions;

const PermissionIcon = ({ allowed }: { allowed: boolean }) =>
  allowed ? (
    <CheckIcon color="success" fontSize="small" />
  ) : (
    <CloseIcon color="error" fontSize="small" />
  );

export const RolesPermissionsTable = () => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const { data, isLoading, error } = useAccessRolesWithPermissionsQuery();

  const flattenedData = useMemo<Array<FlattenedPermissionRow>>(() => {
    if (!data) return [];

    return data.flatMap((role: AccessRoleWithPermissionsDto) =>
      role.accessRights.length > 0
        ? role.accessRights.map(
            (right: AccessRightPermissionDto): FlattenedPermissionRow => ({
              roleId: role.accessRoleID,
              roleCode: role.code,
              roleName: role.name,
              accessRightCode: right.code,
              canRead: right.canRead,
              canCreate: right.canCreate,
              canUpdate: right.canUpdate,
              canDelete: right.canDelete,
              canExport: right.canExport,
            }),
          )
        : [
            {
              roleId: role.accessRoleID,
              roleCode: role.code,
              roleName: role.name,
              accessRightCode: t(RolesPermissionsResources.NoAccessRights),
              canRead: false,
              canCreate: false,
              canUpdate: false,
              canDelete: false,
              canExport: false,
            },
          ],
    );
  }, [data, t]);

  const columns = useMemo<Array<MRT_ColumnDef<FlattenedPermissionRow>>>(
    () => [
      {
        accessorKey: "roleName",
        header: t(RolesPermissionsResources.TableHeaders.RoleName),
        size: 150,
        GroupedCell: ({ row }) => {
          const roleCode = row.original.roleCode;
          const accessRightsCount = row.subRows?.length || 0;
          return (
            <Typography
              fontSize={14}
              fontWeight={500}
              display="flex"
              gap={1}
              alignItems="center"
            >
              <span>{roleCode}</span>

              <Typography component="span" fontSize={12} color="text.secondary">
                • {accessRightsCount}{" "}
                {accessRightsCount === 1
                  ? t(RolesPermissionsResources.AccessRightSingular)
                  : t(RolesPermissionsResources.AccessRightPlural)}
              </Typography>
            </Typography>
          );
        },
      },
      {
        accessorKey: "accessRightCode",
        header: t(RolesPermissionsResources.TableHeaders.AccessRightCode),
        size: 150,
        enableGrouping: false,
      },
      {
        accessorKey: "canExport",
        header: t(RolesPermissionsResources.TableHeaders.CanExport),
        Header: () => (
          <Tooltip title={t(RolesPermissionsResources.TableHeaders.CanExport)}>
            <FileDownloadIcon fontSize="small" />
          </Tooltip>
        ),
        size: 30,
        enableGrouping: false,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => <PermissionIcon allowed={row.original.canExport} />,
      },
      {
        accessorKey: "canDelete",
        header: t(RolesPermissionsResources.TableHeaders.CanDelete),
        Header: () => (
          <Tooltip title={t(RolesPermissionsResources.TableHeaders.CanDelete)}>
            <DeleteIcon fontSize="small" />
          </Tooltip>
        ),
        size: 30,
        enableGrouping: false,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => <PermissionIcon allowed={row.original.canDelete} />,
      },
      {
        accessorKey: "canUpdate",
        header: t(RolesPermissionsResources.TableHeaders.CanUpdate),
        Header: () => (
          <Tooltip title={t(RolesPermissionsResources.TableHeaders.CanUpdate)}>
            <EditIcon fontSize="small" />
          </Tooltip>
        ),
        size: 30,
        enableGrouping: false,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => <PermissionIcon allowed={row.original.canUpdate} />,
      },
      {
        accessorKey: "canCreate",
        header: t(RolesPermissionsResources.TableHeaders.CanCreate),
        Header: () => (
          <Tooltip title={t(RolesPermissionsResources.TableHeaders.CanCreate)}>
            <AddIcon fontSize="small" />
          </Tooltip>
        ),
        size: 30,
        enableGrouping: false,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => <PermissionIcon allowed={row.original.canCreate} />,
      },
      {
        accessorKey: "canRead",
        header: t(RolesPermissionsResources.TableHeaders.CanRead),
        Header: () => (
          <Tooltip title={t(RolesPermissionsResources.TableHeaders.CanRead)}>
            <VisibilityIcon fontSize="small" />
          </Tooltip>
        ),
        size: 30,
        enableGrouping: false,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) => <PermissionIcon allowed={row.original.canRead} />,
      },
    ],
    [t],
  );

  const table = useMaterialReactTable({
    data: flattenedData,
    columns,
    localization,
    enablePagination: true,
    enableTopToolbar: true,
    enableBottomToolbar: true,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enableGrouping: true,
    renderToolbarInternalActions: () => null,
    muiToolbarAlertBannerProps: { sx: { display: "none" } },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        size: 40,
        grow: false,
      },
    },
    initialState: {
      grouping: ["roleName"],
      columnVisibility: { roleCode: false },
      pagination: { pageSize: 100, pageIndex: 0 },
      density: "compact",
    },
    muiTablePaperProps: {
      sx: {
        borderRadius: "10px",
      },
    },
  });

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        <Typography>{String(error)}</Typography>
      </Alert>
    );
  }

  return <MaterialReactTable table={table} />;
};
